/**
 * WHAT THE COMMITTEE DID TO THE RULEBOOK — the read layer that makes committing real.
 *
 * Reported by Tim on the night of the 2026 committee: "although I committed the rules to record,
 * they still read as provisional". They did, and it was not a display lag. `committedAt` is read
 * in six places and every one of them is a phase check or a gate; `commitPlan` enumerates what
 * WOULD be folded in; the console's arming step promises the fold in the present tense — *"gains a
 * 2026 id, effective immediately… The vote record is attached to each"* — and nothing performed
 * it. The one act the whole evening builds toward wrote a timestamp.
 *
 * NO NEW COLLECTION, and that is the design rather than a shortcut. Everything a decision consists
 * of is already stored on the proposal: which rule it re-voted (`amends`), what happened
 * (`status`), when it was recorded (`recordedAt`), and every vote by name (`votes`). A separate
 * decisions table would be a second copy of facts that already exist, free to drift from them.
 * So the rulebook is `RULES` with the committed proposals applied over it, computed on read.
 *
 * COMMITTED, NOT MERELY RECORDED. An outcome written against an item is the room's decision; the
 * rulebook changes when the docket is committed, which is the deliberate one-way door at the end
 * of the evening. Recording an item and then reopening it — which any commissioner may do — must
 * not have moved the rulebook in between.
 *
 * 2019-I-2 §2 is the rule underneath all of it: a conditional pass carries for one season and
 * returns automatically, and the re-vote needs the full super-majority. Passing it makes it
 * permanent; failing it takes it out of force that night rather than leaving it where it was.
 */

import type { RuleProposal } from '../store/types.js';
import type { RulebookEntry } from './rulebook.js';

export type DecisionKind =
  /** A conditional pass re-voted through: it stops being provisional. */
  | 'made-permanent'
  /** A conditional pass whose re-vote fell short: out of force tonight (2019-I-2 §2). */
  | 'lapsed'
  /** A new proposal that passed outright. */
  | 'enacted'
  /** A new proposal that passed on the relaxed six: in force one season, back next year. */
  | 'enacted-provisional';

export interface Decision {
  kind: DecisionKind;
  /** The rule the decision lands on — an existing id, or the new one it creates. */
  ruleId: string;
  proposalId: string;
  title: string;
  season: number;
  /** `7–2–1`, from the recorded votes. The record the rule carries from now on. */
  tally: string;
  at: string;
}

/** `7–2–1`, or an empty string where nothing was recorded. */
export function tallyOf(p: RuleProposal): string {
  const votes = Object.values((p.votes ?? {}) as Record<string, string>);
  if (votes.length === 0) return '';
  const n = (v: string) => votes.filter((x) => x === v).length;
  return `${n('yes')}–${n('no')}–${n('abstain')}`;
}

/**
 * A new rule's id, in the form the corpus already uses for a passed proposal.
 *
 * Matches `2025-proposal-06-keeper-apron` rather than inventing a scheme: the rulebook is
 * generated, and an id this app makes up would collide with the next regeneration or be orphaned
 * by it.
 */
export function newRuleId(season: number, n: number, title: string): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    .slice(0, 40).replace(/-$/, '');
  return `${season}-proposal-${String(n).padStart(2, '0')}-${slug}`;
}

export interface DecisionInput {
  proposals: readonly RuleProposal[];
  /** Nothing applies until the docket is committed. */
  committedAt?: string;
  /** The running order, so a new rule's number matches its docket position. */
  order?: readonly string[];
}

export function decisionsFrom(input: DecisionInput): Decision[] {
  if (!input.committedAt) return [];
  const order = input.order ?? input.proposals.map((p) => p.id);

  const out: Decision[] = [];
  for (const p of input.proposals) {
    if (!p.recordedAt) continue;
    const at = p.recordedAt;
    const tally = tallyOf(p);
    const n = order.indexOf(p.id) + 1;

    if (p.isRevote) {
      /*
       * A RE-VOTE LANDS ON THE RULE IT RE-VOTED, never on a new one. `amends` carries that id —
       * `carryoverProposal` sets it — and a re-vote with no target is a data fault rather than a
       * silent no-op, so it is skipped and stays visible as a still-provisional rule.
       */
      for (const ruleId of p.amends ?? []) {
        if (p.status === 'passed') {
          out.push({ kind: 'made-permanent', ruleId, proposalId: p.id, title: p.title,
            season: p.season, tally, at });
        } else if (p.status === 'failed') {
          out.push({ kind: 'lapsed', ruleId, proposalId: p.id, title: p.title,
            season: p.season, tally, at });
        }
      }
      continue;
    }

    if (p.status === 'passed' || p.status === 'provisional') {
      out.push({
        kind: p.status === 'passed' ? 'enacted' : 'enacted-provisional',
        ruleId: newRuleId(p.season, n > 0 ? n : out.length + 1, p.title),
        proposalId: p.id,
        title: p.title,
        season: p.season,
        tally,
        at,
      });
    }
  }
  return out;
}

/**
 * The rulebook as it stands after the committee — `RULES` with the decisions applied.
 *
 * Pure, and takes the corpus rather than importing it, so a caller can ask the question of any
 * corpus and a test does not need the real 132.
 */
export function effectiveRules(
  rules: readonly RulebookEntry[],
  decisions: readonly Decision[],
): RulebookEntry[] {
  const byRule = new Map<string, Decision>();
  for (const d of decisions) byRule.set(d.ruleId, d);

  const out = rules.map((r) => {
    const d = byRule.get(r.id);
    if (!d) return r;
    if (d.kind === 'made-permanent') {
      /*
       * `provisional` false AND the outcome cleared. `carryoverRules` matches on
       * `outcome === REVOTE_OUTCOME`, so a rule that kept its 2025 conditional-pass marker would
       * come back onto next year's docket having already been made permanent this year.
       */
      return { ...r, provisional: false, outcome: `made-permanent-${d.season}` };
    }
    if (d.kind === 'lapsed') {
      return { ...r, status: 'rescinded', provisional: false, outcome: `lapsed-${d.season}` };
    }
    return r;
  });

  /* Anything enacted that is not already in the corpus joins it. */
  const have = new Set(rules.map((r) => r.id));
  for (const d of decisions) {
    if (have.has(d.ruleId)) continue;
    if (d.kind !== 'enacted' && d.kind !== 'enacted-provisional') continue;
    out.push({
      id: d.ruleId,
      year: d.season,
      title: d.title,
      status: 'active',
      provisional: d.kind === 'enacted-provisional',
      outcome: d.kind === 'enacted-provisional' ? `conditional-pass-revote-${d.season + 1}` : '',
      supersedes: [],
      supersededBy: [],
      topics: [],
    } as unknown as RulebookEntry);
  }
  return out;
}

/** What a rule carries from the night it was decided, for the row that shows it. */
export function decisionNote(d: Decision): string {
  const when = new Date(d.at).toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
  switch (d.kind) {
    case 'made-permanent':
      return `Made permanent ${when} on a re-vote, ${d.tally}.`;
    case 'lapsed':
      return `Lapsed ${when} — the re-vote fell short at ${d.tally}.`;
    case 'enacted':
      return `Passed ${when}, ${d.tally}.`;
    default:
      return `Passed ${when} for one season only, ${d.tally}.`;
  }
}
